/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Code,
  Palette,
  DollarSign,
  Briefcase,
  Video,
  PenLine,
  Megaphone,
} from 'lucide-react'
import * as Yup from 'yup';
import { Course, CourseLesson, StudyMaterialFilters } from './types';
 
/**
 * Formats a number as currency with ₦ symbol and 2 decimal places
 * @param {number} amount- The number to format
 *
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount: number) => {
  if (!amount) return "0.0";

  return new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
  }).format(amount);
};

/**
 * Parses whatever the API sent into a Date, or null.
 *
 * Several admin screens tested `value instanceof Date` and fell back to
 * `new Date()` when it was false — which it always is for a JSON payload, since
 * the API sends ISO strings and no client hydrates them. The result was an
 * activity feed on which every row read "less than a minute ago" and every
 * `<time datetime>` claimed the present moment. `instanceof Date` is never the
 * right question to ask of a response body; this is.
 */
export const parseDate = (value: unknown): Date | null => {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;

  // Mongo/Firebase-ish shapes that reach the browser as plain objects.
  if (typeof value === 'object') {
    const seconds = (value as any).seconds ?? (value as any)._seconds;
    if (typeof seconds === 'number') return new Date(seconds * 1000);
    if (typeof (value as any).$date === 'string') return parseDate((value as any).$date);
    return null;
  }

  if (typeof value !== 'string' && typeof value !== 'number') return null;

  const date = new Date(value);
  return isNaN(date.getTime()) ? null : date;
};

export const formatDate = (timestamp: any) => {
  if (!timestamp) {
    return "N/A";
  }

  // Check for Firebase server value format
  if (typeof timestamp === "object" && timestamp[".sv"] === "timestamp") {
    // For server values, we'll use current date since the actual
    // timestamp hasn't been set by the server yet
    const date = new Date();
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  }

  let date: Date;

  try {
    if (typeof timestamp === "object" && timestamp.seconds) {
      // Firebase Timestamp object
      date = new Date(timestamp.seconds * 1000);
    } else if (typeof timestamp === "object" && timestamp._seconds) {
      // Another Firebase-like Timestamp object
      date = new Date(timestamp._seconds * 1000);
    } else if (timestamp instanceof Date) {
      // Already a Date object
      date = timestamp;
    } else if (typeof timestamp === "number") {
      // Unix timestamp (milliseconds)
      date = new Date(timestamp);
    } else if (typeof timestamp === "string") {
      // Date string (e.g., "YYYY-MM-DD")
      date = new Date(timestamp);
      if (isNaN(date.getTime())) { // Check if the parsed date is valid
        throw new Error("Invalid date string");
      }
    } else {
      return "N/A"; // Fallback for unhandled types
    }

    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  } catch (error) {
    console.error("Error formatting date:", error);
    return "Invalid Date";
  }
};


/**
 * Formats a number as a percentage with the % symbol
 * @param {number} value - The number to format
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted percentage string
 */
export const formatPercentage = (value, decimals = 2) => {
  const formattedValue = value.toFixed(decimals);
  return formattedValue > 0 ? `+${formattedValue}%` : `${formattedValue}%`;
};

/**
 * Generates a URL-friendly slug from a string.
 */
export const generateSlug = (str: string): string => {
  if (!str) return '';
  return str
    .toString()
    .toLowerCase()
    .trim() // Remove leading/trailing whitespace
    .replace(/\s+/g, '-') // Replace spaces with -
    .replace(/[^\w-]+/g, '') // Remove all non-word chars (except hyphen)
    .replace(/--+/g, '-') // Replace multiple - with single -
    .replace(/^-+/, '') // Trim - from start of text
    .replace(/-+$/, ''); // Trim - from end of text
};

/**
 * Formats large numbers with appropriate suffixes (K, M, B, T)
 * @param {number} value - The number to format
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted large number with appropriate suffix
 */
export const formatLargeNumber = (value, decimals = 2) => {
  if (value === null || value === undefined) return '-';

  if (value === 0) return '0';

  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  const trillion = 1000000000000;
  const billion = 1000000000;
  const million = 1000000;
  const thousand = 1000;

  if (absValue >= trillion) {
    return `${sign}$${(value / trillion).toFixed(decimals)}T`;
  } else if (absValue >= billion) {
    return `${sign}$${(value / billion).toFixed(decimals)}B`;
  } else if (absValue >= million) {
    return `${sign}$${(value / million).toFixed(decimals)}M`;
  } else if (absValue >= thousand) {
    return `${sign}$${(value / thousand).toFixed(decimals)}K`;
  } else {
    return `${sign}$${value.toFixed(decimals)}`;
  }
};



export const isOnline = () => window.navigator.onLine;


/**
 * The course taxonomy, and the interests offered at onboarding.
 *
 * The previous list (Technology, Science, Sports, Travel, Food, Fashion, …) was a
 * generic set that matched nothing in the catalogue: there are no Sports, Travel or
 * Food courses, and no course carried any of those tags, so a learner's chosen
 * interests could never resolve to a course. These seven are derived from what the
 * 60 courses actually teach, and every one of them has courses in it.
 *
 * `name` is the value stored on `courses.categories` and on a user's `interests`,
 * so it must match the database exactly.
 */
export const categories = [
  { id: 1, name: "Design & Creative", icon: <Palette size={20} /> },
  { id: 2, name: "Video & Audio", icon: <Video size={20} /> },
  { id: 3, name: "Writing & Publishing", icon: <PenLine size={20} /> },
  { id: 4, name: "Technology & Data", icon: <Code size={20} /> },
  { id: 5, name: "Business & Money", icon: <DollarSign size={20} /> },
  { id: 6, name: "Marketing & Social Media", icon: <Megaphone size={20} /> },
  { id: 7, name: "Career & Work", icon: <Briefcase size={20} /> },
];


export function base64ToFile(base64String: string, fileName: string): File {
  const matches = base64String.match(/^data:(.+);base64,(.+)$/);
  if (!matches || matches.length !== 3) {
    throw new Error("Invalid base64 format");
  }

  const mimeType: string = matches[1];
  const base64Data: string = matches[2];
  const byteCharacters = atob(base64Data);
  const byteArrays: Uint8Array[] = [];

  for (let offset = 0; offset < byteCharacters.length; offset += 512) {
    const slice = byteCharacters.slice(offset, offset + 512);
    const byteNumbers: number[] = new Array(slice.length);

    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  return new File(byteArrays, fileName, { type: mimeType });
}

export const createTimestamp = (daysAgo) => {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);

  return {
    toDate: () => date,
    seconds: Math.floor(date.getTime() / 1000),
    nanoseconds: 0,
  };
};


export const getFormatFromFileType = (fileType: string): string => {
  if (fileType.includes("pdf")) return "pdf";
  if (fileType.includes("word") || fileType.includes("docx")) return "docx";
  if (fileType.includes("powerpoint") || fileType.includes("pptx"))
    return "pptx";
  return "document";
};



/**
 * Pulls the 11-character video id out of any YouTube URL form we might store.
 *
 * The previous regex left the dot in `youtu.be` unescaped — so it matched any
 * character in that position and happily accepted look-alike hosts — and it knew
 * nothing about /shorts/ or /live/ URLs. Ids are matched on the real id alphabet
 * ([A-Za-z0-9_-]) rather than "anything up to a delimiter", so a malformed URL
 * returns null instead of a truncated id.
 */
export function extractYouTubeId(url: string): string | null {
  if (!url || typeof url !== 'string') return null;

  // A bare id, already extracted.
  if (/^[A-Za-z0-9_-]{11}$/.test(url.trim())) return url.trim();

  const patterns = [
    /youtu\.be\/([A-Za-z0-9_-]{11})/,
    /[?&]v=([A-Za-z0-9_-]{11})/,
    /\/embed\/([A-Za-z0-9_-]{11})/,
    /\/shorts\/([A-Za-z0-9_-]{11})/,
    /\/live\/([A-Za-z0-9_-]{11})/,
    /\/v\/([A-Za-z0-9_-]{11})/,
    /\/u\/\w+\/([A-Za-z0-9_-]{11})/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }

  return null;
}




/**
 * Sanitizes a string to be used as a filename
 */
export const sanitizeForFilename = (str: string): string => {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim()
    .replace(/^-+|-+$/g, '');
};

/**
 * Generates the expected filename for a lesson's MDX content
 */
export const generateLessonFilename = (courseTitle: string, lessonId: string): string => {
  const sanitizedTitle = sanitizeForFilename(courseTitle);
  return `${sanitizedTitle}/${lessonId}.mdx`;
};

/**
 * Loads MDX content for a lesson from the public directory
 */
export const loadLessonContent = async (
  lesson: CourseLesson,
): Promise<string | null> => {
  if (lesson.type !== 'markdown' || !lesson.content) {
    return null;
  }
  try {
  return lesson.content
  } catch (error) {
    console.error(`Error loading content for lesson ${lesson.id}:`, error);
    return null;
  }
};





/**
 * Validates if a course has all required properties
 */
export const validateCourse = (course: any): course is Course => {
  return (
    course &&
    typeof course.id === 'string' &&
    typeof course.title === 'string' &&
    Array.isArray(course.modules) &&
    course.modules.every((module: any) =>
      module &&
      typeof module.id === 'string' &&
      typeof module.title === 'string' &&
      Array.isArray(module.lessons)
    )
  );
};









export const sanitizeFilters = (filters: StudyMaterialFilters): StudyMaterialFilters => {
  const sanitized: StudyMaterialFilters = {};

  if (filters.category?.trim()) sanitized.category = filters.category.trim();
  if (filters.type) sanitized.type = filters.type;
  if (filters.schoolId?.trim()) sanitized.schoolId = filters.schoolId.trim();
  if (filters.courseCode?.trim()) sanitized.courseCode = filters.courseCode.trim().toUpperCase();
  if (filters.courseTitle?.trim()) sanitized.courseTitle = filters.courseTitle.trim();
  if (filters.level) sanitized.level = filters.level;
  if (filters.session?.trim()) sanitized.session = filters.session.trim();
  if (filters.search?.trim()) sanitized.search = filters.search.trim();

  // Validate materialType
  if (filters.materialType === 'document' || filters.materialType === 'pastQuestion') {
    sanitized.materialType = filters.materialType;
  }

  return sanitized;
};



/**
 * Enhanced HTML Cleaner - Comprehensive HTML cleaning with style and structure preservation
 * Handles encoding issues, malformed HTML, and maintains inline styles and proper formatting
 */
function cleanHTML(html, options = {}) {
    // Default options with better preservation settings
    const config = {
        preserveInlineStyles: true,
        preserveClassNames: true,
        preserveTagStructure: true,
        convertToSemantic: true,
        fixEncoding: true,
        removeEmptyElements: false,
        normalizeWhitespace: false,
        addHeadingIds: false,
        wrapTablesResponsive: true,
        maintainFormatting: true,
        preserveImportantSpacing: true,
        ...options
    };

    // Step 1: Fix common encoding issues while preserving structure
    if (config.fixEncoding) {
        const encodingMap = {
            // UTF-8 encoding fixes - ordered by complexity to avoid conflicts
            'Ã¢â‚¬â„¢': "'",      // Complex apostrophe encoding
            'Ã¢â‚¬Å"': '"',       // Complex left quote  
            'Ã¢â‚¬Â': '"',        // Complex right quote
            'Ã¢â‚¬â€œ': '—',      // Complex em dash
            'childrenÃ¢â‚¬â„¢s': "children's", // Specific fix for your content
            'isnt': "isn't",      // Missing apostrophes
            'Thereâ€™s': "There's",
            'elseâ€™s': "else's",
            'â€œalways-onâ€': '"always-on"',
            'â€œconnectedâ€': '"connected"',
            'â€œcomparison trapâ€': '"comparison trap"',
            'reelsâ€"vacations': 'reels—vacations',
            'filtersâ€"leading': 'filters—leading',
            'â€¢': '•',           // Bullet points
            'â€œ': '"',           // Left double quote
            'â€': '"',            // Right double quote  
            'â€™': "'",           // Right single quote/apostrophe
            'â€˜': "'",           // Left single quote
          'â€"': '—',           // Em dash
          'â€¦': '…',           // Ellipsis
            'â‚¬': '€',           // Euro symbol
            // Degree symbol
            '': '', // Remove unwanted character
            'Â®': '®',            // Registered trademark
            'Â©': '©',            // Copyright
            'Â£': '£',            // Pound sterling
            'â‰¤': '≤',           // Less than or equal
            'â‰¥': '≥',           // Greater than or equal
            'Ã¡': 'á',            // a with acute
            'Ã©': 'é',            // e with acute
            'Ã­': 'í',            // i with acute
            'Ã³': 'ó',            // o with acute
            'Ãº': 'ú',            // u with acute
            'Ã±': 'ñ',            // n with tilde
            '&amp;amp;': '&',     // Triple-encoded ampersand
            '&amp;': '&',         // Double-encoded ampersand
            '&lt;': '<',          // Less than
            '&gt;': '>',          // Greater than
        };

        // Apply fixes in order to handle complex encodings first
        Object.entries(encodingMap).forEach(([encoded, decoded]) => {
            html = html.replace(new RegExp(encoded.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), decoded);
        });

        // Handle &nbsp; more carefully - preserve in certain contexts
        if (!config.preserveImportantSpacing) {
            html = html.replace(/&nbsp;/g, ' ');
        }
    }

    // Step 2: Intelligent whitespace normalization
    if (config.normalizeWhitespace && !config.maintainFormatting) {
        // Only normalize excessive whitespace, preserve intentional spacing
        html = html.replace(/[ \t]{3,}/g, '  ');             // Reduce excessive spaces but keep some
        html = html.replace(/\n{3,}/g, '\n\n');             // Reduce excessive line breaks
        html = html.replace(/^\s+|\s+$/g, '');               // Trim start/end only
    } else if (config.maintainFormatting) {
        // Minimal whitespace cleanup while preserving structure
        html = html.replace(/[ \t]{5,}/g, '    ');           // Only reduce very excessive spaces
        html = html.replace(/\n{4,}/g, '\n\n\n');           // Allow up to 3 line breaks
    }

    // Step 3: Preserve inline styles and class names
    if (config.preserveInlineStyles) {
        // Clean up malformed style attributes instead of removing them
        html = html.replace(/style="([^"]*)"/gi, function(match, styleContent) {
            // Clean up style content but preserve it
            const cleaned = styleContent
                .replace(/;;+/g, ';')                        // Remove multiple semicolons
                .replace(/:\s*;/g, ': ;')                    // Fix empty values
                .replace(/([^;])\s*$/g, '$1;')               // Ensure ending semicolon
                .trim();
            
            return cleaned ? `style="${cleaned}"` : '';
        });
    }

    // Step 4: Preserve class names and clean them up
    if (config.preserveClassNames) {
        html = html.replace(/class="([^"]*)"/gi, function(match, classContent) {
            // Clean up class names but preserve them
            const cleaned = classContent
                .replace(/\s+/g, ' ')                        // Normalize spaces in class names
                .trim();
            
            return cleaned ? `class="${cleaned}"` : '';
        });
    }

    // Step 5: Enhanced semantic conversion that preserves existing structure
    if (config.convertToSemantic) {
     
        // Convert bullet points while preserving structure and styling
        html = convertBulletPointsToLists(html, config);
        
        // Convert numbered lists while preserving structure and styling  
        html = convertNumberedLists(html, config);
    }

    // Step 6: Remove empty paragraphs while preserving styled ones
    if (config.removeEmptyElements) {
        // Remove truly empty paragraphs but keep ones that might be used for spacing
        html = html.replace(/<p><\/p>/gi, '');
        html = html.replace(/<p>\s*<\/p>/gi, '');
        
        // More conservative cleanup for other elements
        html = html.replace(/<span[^>]*>\s*<\/span>/gi, function(match) {
            // Don't remove if it has styling
            if (match.includes('style=') || match.includes('class=')) {
                return match;
            }
            return '';
        });
        
        // More conservative br tag cleanup
        html = html.replace(/(<br[^>]*>){4,}/gi, '<br><br><br>');
    } else {
        // Even when not removing empty elements, clean up excessive empty paragraphs
        html = html.replace(/(<p><\/p>\s*){3,}/gi, '<p></p><p></p>');
    }

    // Step 7: Add semantic improvements
    if (config.addHeadingIds) {
        html = addHeadingIds(html);
    }

    // Step 8: Wrap tables for responsiveness while preserving attributes
    if (config.wrapTablesResponsive) {
        html = html.replace(/<table([^>]*)>/gi, function(match, attrs) {
            return `<div class="table-responsive"><table${attrs}>`;
        });
        html = html.replace(/<\/table>/gi, '</table></div>');
    }

    // Step 9: Final formatting - preserve structure
    if (config.maintainFormatting) {
        // Add minimal line breaks for readability without destroying layout
        html = html.replace(/(<\/(?:h[1-6]|p|div|section|article)>)(?!\s*\n)/gi, '$1\n');
        html = html.replace(/(<(?:h[1-6]|p|div|section|article)[^>]*>)/gi, '\n$1');
        html = html.replace(/^\n+|\n+$/g, ''); // Clean up leading/trailing newlines
        html = html.replace(/\n{3,}/g, '\n\n'); // Limit to double line breaks
    }

    return html.trim();
}

/**
 * Convert bullet point paragraphs to proper HTML lists while preserving styling
 */
function convertBulletPointsToLists(html, config) {
    // Pattern to match bullet points in your specific format
    const bulletPattern = /<p><span([^>]*)>â€¢([^<]*)<\/span><\/p>/gi;
    
    // Find sequences of bullet points
    let result = html;
    const foundBullets = [];
    let match;
    
    // Reset regex
    bulletPattern.lastIndex = 0;
    
    while ((match = bulletPattern.exec(html)) !== null) {
        foundBullets.push({
            fullMatch: match[0],
            attrs: match[1],
            text: match[2].trim(),
            index: match.index
        });
    }
    
    if (foundBullets.length === 0) {
        return html;
    }
    
    // Group consecutive bullet points
    const groups = [];
    let currentGroup = [foundBullets[0]];
    
    for (let i = 1; i < foundBullets.length; i++) {
        const prevEnd = foundBullets[i-1].index + foundBullets[i-1].fullMatch.length;
        const currentStart = foundBullets[i].index;
        
        // Check if there's only whitespace/empty paragraphs between bullets
        const between = html.substring(prevEnd, currentStart);
        if (/^(\s|<p><\/p>)*$/i.test(between)) {
            currentGroup.push(foundBullets[i]);
        } else {
            groups.push(currentGroup);
            currentGroup = [foundBullets[i]];
        }
    }
    groups.push(currentGroup);
    
    // Replace each group with a proper list
    for (const group of groups.reverse()) { // Reverse to avoid index shifting
        const listItems = group.map(bullet => {
            if (bullet.attrs && config.preserveInlineStyles) {
                return `  <li><span${bullet.attrs}>${bullet.text}</span></li>`;
            } else {
                return `  <li>${bullet.text}</li>`;
            }
        }).join('\n');
        
        const listHtml = `<ul>\n${listItems}\n</ul>`;
        
        // Find the range to replace (from first bullet to last bullet in group)
        const startIndex = group[0].index;
        const endIndex = group[group.length - 1].index + group[group.length - 1].fullMatch.length;
        
        // Include any <p></p> tags between bullets in the replacement
        const rangeToReplace = html.substring(startIndex, endIndex);
        
        result = result.replace(rangeToReplace, listHtml);
    }
    
    return result;
}

/**
 * Convert numbered list paragraphs to proper HTML ordered lists while preserving styling
 */
function convertNumberedLists(html, config) {
    // Pattern for your specific numbered list format
    const numberedPattern = /<p><span([^>]*)>(\d+\.?\s+[^<]*)<\/span><\/p>/gi;
    
    // Find sequences of numbered items
    let result = html;
    const foundNumbers = [];
    let match;
    
    // Reset regex
    numberedPattern.lastIndex = 0;
    
    while ((match = numberedPattern.exec(html)) !== null) {
        const text = match[2].trim();
        const numberMatch = text.match(/^(\d+)\.?\s+(.*)$/);
        
        if (numberMatch) {
            foundNumbers.push({
                fullMatch: match[0],
                attrs: match[1],
                number: parseInt(numberMatch[1]),
                text: numberMatch[2].trim(),
                index: match.index
            });
        }
    }
    
    if (foundNumbers.length === 0) {
        return html;
    }
    
    // Group consecutive numbered items
    const groups = [];
    let currentGroup = [foundNumbers[0]];
    
    for (let i = 1; i < foundNumbers.length; i++) {
        const prevItem = foundNumbers[i-1];
        const currentItem = foundNumbers[i];
        
        const prevEnd = prevItem.index + prevItem.fullMatch.length;
        const currentStart = currentItem.index;
        
        // Check if there's only whitespace/empty paragraphs between items
        const between = html.substring(prevEnd, currentStart);
        const isConsecutive = /^(\s|<p><\/p>)*$/i.test(between);
        
        // Check if numbers are sequential (allowing for some gaps)
        const isSequential = currentItem.number <= prevItem.number + 3;
        
        if (isConsecutive && isSequential) {
            currentGroup.push(currentItem);
        } else {
            groups.push(currentGroup);
            currentGroup = [currentItem];
        }
    }
    groups.push(currentGroup);
    
    // Replace each group with a proper ordered list
    for (const group of groups.reverse()) { // Reverse to avoid index shifting
        const listItems = group.map(item => {
            if (item.attrs && config.preserveInlineStyles) {
                return `  <li><span${item.attrs}>${item.text}</span></li>`;
            } else {
                return `  <li>${item.text}</li>`;
            }
        }).join('\n');
        
        const listHtml = `<ol>\n${listItems}\n</ol>`;
        
        // Find the range to replace
        const startIndex = group[0].index;
        const endIndex = group[group.length - 1].index + group[group.length - 1].fullMatch.length;
        
        const rangeToReplace = html.substring(startIndex, endIndex);
        result = result.replace(rangeToReplace, listHtml);
    }
    
    return result;
}

/**
 * Add IDs to headings for navigation while preserving existing attributes
 */
function addHeadingIds(html) {
    return html.replace(/<h([1-6])([^>]*)>([^<]+)<\/h[1-6]>/gi, function(match, level, attrs, text) {
        // Check if ID already exists
        if (attrs.includes('id=')) {
            return match;
        }
        
        const id = text.toLowerCase()
            .replace(/[^\w\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '');
            
        const newAttrs = attrs.trim() ? `${attrs} id="${id}"` : ` id="${id}"`;
        return `<h${level}${newAttrs}>${text}</h${level}>`;
    });
}

/**
 * Comprehensive HTML cleaner with validation and better error handling
 */
interface CleanPostHTMLOptions {
    validateOutput?: boolean;
    preserveInlineStyles?: boolean;
    preserveClassNames?: boolean;
    preserveTagStructure?: boolean;
    maintainFormatting?: boolean;
    preserveImportantSpacing?: boolean;
    [key: string]: any;
}

export function cleanPostHTML(html: string, options: CleanPostHTMLOptions = {}) {
    try {
        // Input validation
        if (typeof html !== 'string') {
            throw new Error('Input must be a string');
        }
        
        if (html.trim() === '') {
            return '';
        }

        // Set default options that preserve more structure
        const defaultOptions = {
            preserveInlineStyles: true,
            preserveClassNames: true,
            preserveTagStructure: true,
            maintainFormatting: true,
            preserveImportantSpacing: true,
            removeEmptyElements: false,
            normalizeWhitespace: false,
            ...options
        };

        // Clean the HTML with preservation-focused settings
        const cleaned = cleanHTML(html, defaultOptions);
        
        // Enhanced validation
        if (options?.validateOutput !== false) {
            try {
                const parser = new DOMParser();
                const doc = parser.parseFromString(cleaned, 'text/html');
                const errors = doc.querySelectorAll('parsererror');
                
                if (errors.length > 0) {
                    console.warn('HTML parsing warnings detected:', errors);
                    // Don't fail completely, just warn
                }
                
                // Additional validation - check for unclosed tags
                const openTags = (cleaned.match(/<[^/!][^>]*>/g) || []).length;
                const closeTags = (cleaned.match(/<\/[^>]*>/g) || []).length;
                const selfClosing = (cleaned.match(/<[^>]*\/>/g) || []).length;
                
                if (Math.abs(openTags - closeTags - selfClosing) > 2) {
                    console.warn('Potential tag mismatch detected, but proceeding with cleaned HTML');
                }
                
            } catch (validationError) {
                console.warn('HTML validation failed, but returning cleaned HTML:', validationError);
            }
        }

        return cleaned;
        
    } catch (error) {
        console.error('HTML cleaning failed:', error);
        // Return original HTML with minimal cleanup
        return html.replace(/â€¢/g, '•').replace(/â€œ/g, '"').replace(/â€/g, '"');
    }
}

// Export additional utility functions
export { cleanHTML, convertBulletPointsToLists, convertNumberedLists, addHeadingIds };



export const courseSchema = Yup.object().shape({
    title: Yup.string().required('Course title is required'),
    description: Yup.string().required('Course description is required'),
    category: Yup.string().required('Category is required'), 
    price: Yup.number().min(0, 'Price cannot be negative').required('Price is required'),
    discount: Yup.number().min(0, 'Discount cannot be negative').max(100, 'Discount cannot exceed 100%').optional(),
    currency: Yup.string().required('Currency is required'),
    level: Yup.string().oneOf(['beginner', 'intermediate', 'advanced', 'all-levels']).required('Course level is required'),
    duration: Yup.number().min(0, 'Duration cannot be negative').required('Duration is required (in minutes)'),
    instructor: Yup.string().required('Instructor name is required'),
    thumbnailFile: Yup.mixed().optional().nullable(),
    modules: Yup.array().of(
        Yup.object().shape({
            title: Yup.string().required('Module title is required'),
            description: Yup.string().optional(),
            lessons: Yup.array().of(
                Yup.object().shape({
                    title: Yup.string().required('Lesson title is required'),
                    // Updated to include 'quiz' as a lesson type
                    type: Yup.string().oneOf(['markdown', 'text', 'quiz']).required('Lesson type is required'),
                    content: Yup.string().when('type', {
                        is: (val) => val === 'markdown' || val === 'text',
                        then: (schema) => schema.required('Content is required for markdown/text lessons.'),
                        otherwise: (schema) => schema.optional().nullable(),
                    }),
                    quiz: Yup.object().when('type', {
                        is: 'quiz',
                        then: (schema) => schema.shape({
                            title: Yup.string().required('Quiz title is required'),
                            questions: Yup.array().of(
                                Yup.object().shape({
                                    questionText: Yup.string().required('Question text is required'),
                                    questionType: Yup.string().oneOf(['multiple-choice', 'single-choice', 'true-false']).required('Question type is required'),
                                    options: Yup.array().when('questionType', {
                                        is: (val) => val === 'multiple-choice' || val === 'single-choice',
                                        then: (schema) => schema.of(
                                            Yup.object().shape({
                                                text: Yup.string().required('Option text is required'),
                                                isCorrect: Yup.boolean(),
                                            })
                                        ).min(2, 'At least two options are required').test(
                                            'has-correct-answer',
                                            'At least one option must be marked as correct for single/multiple choice.',
                                            function (options) {
                                                if (this.parent.questionType === 'true-false') return true;
                                                return options?.some(opt => opt.isCorrect) || false;
                                            }
                                        ),
                                        otherwise: (schema) => schema.optional(),
                                    }),
                                    correctAnswerId: Yup.string().when('questionType', { // Changed from correctAnswer to correctAnswerId
                                        is: 'true-false', // Should be correctAnswerId for true-false as well if options are used
                                        then: (schema) => schema.required('Correct answer is required for True/False'),
                                        otherwise: (schema) => schema.optional().nullable(),
                                    }),
                                })
                            ).min(1, 'At least one question is required for a quiz'),
                        }).required('Quiz object is required for lesson of type "quiz"'),
                        otherwise: (schema) => schema.optional().nullable(),
                    }),
                })
            ).min(1, 'At least one lesson is required per module')
            .test('module-quiz-validation', 'A module can only have one quiz, and it must be the last lesson.', function (lessons) {
                if (!lessons || lessons.length === 0) {
                    return true; // No lessons, so no quiz issues
                }
                const quizLessons = lessons.filter(lesson => lesson.type === 'quiz');
                if (quizLessons.length > 1) {
                    return this.createError({ message: 'A module can only have one quiz.' });
                }
                if (quizLessons.length === 1) {
                    const quizIndex = lessons.findIndex(lesson => lesson.type === 'quiz');
                    if (quizIndex !== lessons.length - 1) {
                        return this.createError({ message: 'The quiz must be the last lesson in the module.' });
                    }
                    // Ensure the lesson marked as type 'quiz' actually has a quiz object
                    if (!lessons[quizIndex].quiz) {
                        return this.createError({ path: `lessons[${quizIndex}].quiz`, message: 'Lesson of type "quiz" must have a quiz definition.' });
                    }
                }
                // Ensure lessons not marked as type 'quiz' do not have a quiz object
                for (let i = 0; i < lessons.length; i++) {
                    if (lessons[i].type !== 'quiz' && lessons[i].quiz) {
                        return this.createError({ path: `lessons[${i}].quiz`, message: 'Only lessons of type "quiz" can have a quiz definition.' });
                    }
                }
                return true;
            }),
        })
    ).min(1, 'At least one module is required'),
});